import { falAngleLeft } from 'shared/@generated/icons/falAngleLeft';
import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { falRotateLeft } from 'shared/@generated/icons/falRotateLeft';
import { Platform, View } from 'react-native';
import { H1 } from 'shared/components/title';
import { Text } from 'shared/components/text';

import { useRoute } from 'shared/library/routes/route';
import { useRouter } from 'shared/library/use-router';
import { PhoneInput } from 'shared/components/form/phone-input';
import { Image } from 'shared/components/image';
import { ReviewsSummaryCard } from 'shared/components/reviews-summary-card';
import React, {
  forwardRef,
  useEffect,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocale } from 'shared/library/i18n/use-locale';
import useApi from 'shared/library/use-api';
import { apiRoute } from 'shared/library/api-route';
import { InputError } from 'shared/components/form/input-error';
import { useTranslation } from 'react-i18next';
import { Trans } from 'react-i18next';
import { Link } from 'shared/components/link';
import { Button } from 'shared/components/button';
import { useLocalStorage } from 'shared/library/use-local-storage';
import { useEffectOnce } from 'shared/library/use-effect-once';
import { notify } from 'shared/library/notify';
import { clsx } from 'clsx';
import { staticImage } from 'shared/library/static-images';
import { analytics } from 'shared/library/analytics';
import { keychain } from 'shared/library/keychain';
import { useCountdown } from 'shared/library/use-countdown';
import { CodeInput, CodeInputRef } from 'shared/components/form/code-input';
import useLoginStore from 'shared/state/login';
import { monitoring } from 'shared/library/monitoring';
import { useUserStore } from 'shared/state/user';
import { runAfterInteractions } from 'shared/library/run-after-interactions';
import { contactTracking } from 'shared/library/contact-tracking';
import { getContactErrorType } from 'shared/library/contact-attempt';
import { FormWizardDescription, FormWizardTitle } from 'shared/components/form/form-wizard-title';
import { LoginCompletionContext } from 'shared/library/login-completion-context';

export interface LoginScreenRef {
  submit: () => Promise<void>;
}

export interface LoginScreenState {
  step: 'phone' | 'code';
  phoneNumber: string;
  codeSentToPhoneNumber: string | null;
  codeSentAt: number | null;
}

interface LoginScreenProps {
  isInModal?: boolean;
  isInWizard?: boolean;
  redirectTo?: string;
  phoneTitle?: string;
  phoneDescription?: string;
  onBusyChange?: (busy: boolean) => void;
  initialState?: LoginScreenState;
  onStateChange?: (state: LoginScreenState) => void;
}

type AgreementPart =
  | {
      type: 'text';
      text: string;
    }
  | {
      type: 'terms' | 'privacy';
      text: string;
    };

const agreementTagPattern = /<(terms|privacy)>(.*?)<\/\1>/g;
const resendCodeCooldownMs = 1000 * 60;

function trackLoginEvent(name: string) {
  try {
    analytics.event(name);
  } catch {
    // Analytics must not interrupt authentication or change its recorded outcome.
  }
}

function parseAgreementParts(value: string): AgreementPart[] {
  const parts: AgreementPart[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;

  agreementTagPattern.lastIndex = 0;

  while ((match = agreementTagPattern.exec(value)) !== null) {
    if (match.index > cursor) {
      parts.push({
        type: 'text',
        text: value.slice(cursor, match.index),
      });
    }

    parts.push({
      type: match[1] as 'terms' | 'privacy',
      text: match[2] ?? '',
    });

    cursor = match.index + match[0].length;
  }

  if (cursor < value.length) {
    parts.push({
      type: 'text',
      text: value.slice(cursor),
    });
  }

  return parts.filter((part) => part.text.length > 0);
}

function getRedirectParam(value: unknown) {
  const rawValue = Array.isArray(value) ? value[0] : value;

  if (
    typeof rawValue !== 'string' ||
    rawValue === '' ||
    !rawValue.startsWith('/') ||
    rawValue.startsWith('//') ||
    rawValue.includes('\\') ||
    /[\u0000-\u001f\u007f]/.test(rawValue)
  ) {
    return null;
  }

  return rawValue;
}

const DeferredReviewsSummaryCard = () => {
  const [shouldRender, setShouldRender] = useState(Platform.OS === 'web');

  useEffect(() => {
    if (Platform.OS === 'web') {
      return undefined;
    }

    return runAfterInteractions(() => {
      setShouldRender(true);
    });
  }, []);

  if (!shouldRender) {
    return null;
  }

  return <ReviewsSummaryCard />;
};

const LoginHeading = ({
  title,
  subtitle,
  isInModal,
  isInWizard = false,
}: {
  title: React.ReactNode;
  subtitle: React.ReactNode;
  isInModal: boolean;
  isInWizard?: boolean;
}) => {
  if (isInWizard) {
    return (
      <>
        <FormWizardTitle>{title}</FormWizardTitle>
        <FormWizardDescription>{subtitle}</FormWizardDescription>
      </>
    );
  }

  // The login modal shows the step heading in its header.
  if (isInModal) return null;

  return <H1 title={title} subtitle={subtitle} marginTop={false} />;
};

export const LoginScreen = forwardRef<LoginScreenRef, LoginScreenProps>(
  (
    {
      isInModal = false,
      isInWizard = false,
      redirectTo,
      phoneTitle,
      phoneDescription,
      onBusyChange,
      initialState,
      onStateChange,
    },
    ref,
  ) => {
    const { t } = useTranslation('screens');
    const { market } = useLocale();
    const [loginState, setLoginState] = useState<LoginScreenState>(
      () =>
        initialState ?? {
          step: 'phone',
          phoneNumber: `+${market.calling_code}`,
          codeSentToPhoneNumber: null,
          codeSentAt: null,
        },
    );
    const { step, phoneNumber, codeSentToPhoneNumber, codeSentAt } = loginState;
    const { setStoredValue } = useLocalStorage<string>('auth_state');
    const completeLogin = useContext(LoginCompletionContext);
    const submitRef = useRef<LoginScreenRef>(null);

    function updateLoginState(update: Partial<LoginScreenState>) {
      // Only retain the SMS destination and timing; the entered code stays inside CodeInput.
      const nextState = { ...loginState, ...update };
      setLoginState(nextState);
      onStateChange?.(nextState);
    }

    useImperativeHandle(ref, () => ({
      submit: async () => {
        await submitRef.current?.submit();
      },
    }));

    useEffectOnce(() => {
      if (!completeLogin) setStoredValue('guest');

      monitoring.startReplay('login');
    });

    const loginForm = (
      <>
        {step === 'code' && (
          <EnterCode
            phoneNumber={phoneNumber}
            codeSentAt={codeSentAt}
            isInModal={isInModal}
            isInWizard={isInWizard}
            redirectTo={redirectTo}
            submitRef={submitRef}
            onBusyChange={onBusyChange}
            onCodeSent={(sentAt) => updateLoginState({ codeSentAt: sentAt })}
            onBack={() => updateLoginState({ step: 'phone' })}
          />
        )}

        {step === 'phone' && (
          <EnterPhoneNumber
            phoneNumber={phoneNumber}
            hasCodeForPhoneNumber={codeSentToPhoneNumber === phoneNumber}
            isInModal={isInModal}
            isInWizard={isInWizard}
            redirectTo={redirectTo}
            submitRef={submitRef}
            onBusyChange={onBusyChange}
            title={phoneTitle}
            description={phoneDescription}
            showReviews={!isInWizard && (Platform.OS === 'web' || !isInModal)}
            onChangePhoneNumber={(value) => updateLoginState({ phoneNumber: value })}
            onCodeSent={() => {
              updateLoginState({
                codeSentToPhoneNumber: phoneNumber,
                codeSentAt: Date.now(),
                step: 'code',
              });
            }}
            onContinue={() => updateLoginState({ step: 'code' })}
          />
        )}
      </>
    );

    if (isInWizard) return loginForm;

    return (
      <View className={clsx('p-3', { 'min-h-body md:p-6 lg:py-20': !isInModal })}>
        <View
          className={clsx('mx-auto w-full max-w-screen-xl gap-6', {
            'md:flex-row-reverse md:gap-6': !isInModal,
          })}
        >
          <View className={'md:flex-1'}>{loginForm}</View>

          {!isInModal && (
            <View className={'md:w-1/2'}>
              <Image
                src={staticImage('puppy-by-table')}
                alt={t('login.image_alt')}
                containerClassName={'aspect-4/3 w-full'}
                imageClassName={'rounded object-cover md:rounded-xl'}
                sizeType={'large'}
                priority={true}
              />
            </View>
          )}
        </View>
      </View>
    );
  },
);

LoginScreen.displayName = 'LoginScreen';

const EnterPhoneNumber = ({
  phoneNumber,
  hasCodeForPhoneNumber,
  onChangePhoneNumber,
  onCodeSent,
  onContinue,
  showReviews,
  isInModal,
  isInWizard = false,
  redirectTo,
  title,
  description,
  submitRef,
  onBusyChange,
}: {
  phoneNumber: string;
  hasCodeForPhoneNumber: boolean;
  onChangePhoneNumber: (v: string) => void;
  onCodeSent: () => void;
  onContinue: () => void;
  showReviews: boolean;
  isInModal: boolean;
  isInWizard?: boolean;
  redirectTo?: string;
  title?: string;
  description?: string;
  submitRef?: React.Ref<LoginScreenRef>;
  onBusyChange?: (busy: boolean) => void;
}) => {
  const { t } = useTranslation('screens');
  const api = useApi({ notifyOnServerErrors: true, settleAfterUnmount: true });
  const sendInFlight = useRef(false);
  const isMounted = useRef(false);
  const route = useRoute();
  const agreementParts = useMemo(() => parseAgreementParts(t('login.agreement')), [t]);
  const router = useRouter();
  const redirectOnSuccess = useLoginStore((state) => state.redirectOnSuccess);
  const redirectAfterLogin = getRedirectParam(
    redirectTo ?? redirectOnSuccess ?? router.query.redirect,
  );

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    onBusyChange?.(api.loading);
    return () => onBusyChange?.(false);
  }, [api.loading, onBusyChange]);

  const sendCode = async () => {
    if (sendInFlight.current) return;

    if (hasCodeForPhoneNumber) {
      onContinue();
      return;
    }

    sendInFlight.current = true;
    const sendParams = { contact_login_send_type: 'initial' };
    contactTracking.trackLogin('contact_login_code_started', redirectAfterLogin, sendParams);

    try {
      await api.request(apiRoute('authentication.send'), { phone_number: phoneNumber });
    } catch (err) {
      contactTracking.trackLogin('contact_login_code_failed', redirectAfterLogin, {
        ...sendParams,
        contact_error_type: getContactErrorType(err),
      });
      return;
    } finally {
      sendInFlight.current = false;
    }

    trackLoginEvent('login_code_sent');
    contactTracking.trackLogin('contact_login_code_sent', redirectAfterLogin, sendParams);
    if (isMounted.current) onCodeSent();
  };

  useImperativeHandle(submitRef, () => ({ submit: sendCode }));

  const input = (
    <PhoneInput
      value={phoneNumber}
      onSubmitEditing={sendCode}
      autoFocus={Platform.OS === 'web'}
      onChange={(val) => {
        onChangePhoneNumber(val);
        api.clearErrors();
      }}
    />
  );

  return (
    <>
      <LoginHeading
        title={title ?? t('login.phone_title')}
        subtitle={description ?? t('login.login_with_phone')}
        isInModal={isInModal}
        isInWizard={isInWizard}
      />

      {isInWizard ? <View className={'mt-6'}>{input}</View> : input}

      <InputError errors={api.errors} />

      {!isInWizard && (
        <Button
          className={'mt-3'}
          text={t('login.continue_with_sms')}
          icon={falAngleRight}
          onPress={sendCode}
          loading={api.loading}
        />
      )}

      {Platform.OS === 'web' ? (
        <Text className={'text-brand-brown-dark/70 mt-3 text-center text-sm'}>
          <Trans
            ns={'screens'}
            i18nKey={'login.agreement'}
            components={{
              terms: (
                <Link
                  href={route('policies.terms')}
                  openInNewTab={true}
                  skipDefaults={true}
                  textClassName={'underline'}
                />
              ),
              privacy: (
                <Link
                  href={route('policies.privacy')}
                  openInNewTab={true}
                  skipDefaults={true}
                  textClassName={'underline'}
                />
              ),
            }}
          />
        </Text>
      ) : (
        <View className={'mt-3 flex-row flex-wrap justify-center px-3'}>
          {agreementParts.map((part, index) => {
            const textClassName = 'text-brand-brown-dark/70 text-center text-sm';

            if (part.type === 'terms' || part.type === 'privacy') {
              return (
                <Link
                  key={`${part.type}-${index}`}
                  href={route(part.type === 'terms' ? 'policies.terms' : 'policies.privacy')}
                  asInline={true}
                  openInNewTab={true}
                  skipDefaults={true}
                  textClassName={`${textClassName} underline`}
                >
                  {part.text}
                </Link>
              );
            }

            return (
              <Text key={`text-${index}`} className={textClassName}>
                {part.text}
              </Text>
            );
          })}
        </View>
      )}

      {showReviews && (
        <View className={'mt-6 flex-row items-center justify-center'}>
          <DeferredReviewsSummaryCard />
        </View>
      )}
    </>
  );
};

const EnterCode = ({
  phoneNumber,
  codeSentAt,
  isInModal,
  isInWizard = false,
  onCodeSent,
  onBack,
  redirectTo,
  submitRef,
  onBusyChange,
}: {
  phoneNumber: string;
  codeSentAt: number | null;
  isInModal: boolean;
  isInWizard?: boolean;
  onCodeSent: (sentAt: number) => void;
  onBack: () => void;
  redirectTo?: string;
  submitRef?: React.Ref<LoginScreenRef>;
  onBusyChange?: (busy: boolean) => void;
}) => {
  const { t } = useTranslation('screens');
  const api = useApi({ notifyOnServerErrors: true, settleAfterUnmount: true });
  const verifyInFlight = useRef(false);
  const isMounted = useRef(false);
  const route = useRoute();
  const codeInputRef = useRef<CodeInputRef>(null);
  const [successful, setSuccessful] = useState(false);
  const redirectOnSuccess = useLoginStore((state) => state.redirectOnSuccess);
  const resetLoginState = useLoginStore((state) => state.reset);
  const router = useRouter();
  const redirectAfterLogin = getRedirectParam(
    redirectTo ?? redirectOnSuccess ?? router.query.redirect,
  );
  const setApiKey = useUserStore((state) => state.setApiKey);
  const setUser = useUserStore((state) => state.setUser);
  const completeLogin = useContext(LoginCompletionContext);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => () => onBusyChange?.(false), [onBusyChange]);

  const verifyCode = async (code: string) => {
    if (verifyInFlight.current || successful) return;
    verifyInFlight.current = true;
    onBusyChange?.(true);
    contactTracking.trackLogin('contact_login_verify_started', redirectAfterLogin);

    let res: { token: string; was_created: boolean };
    try {
      res = await api.request(apiRoute('authentication.verify'), {
        identifier: phoneNumber,
        password: code,
      });
    } catch (err) {
      verifyInFlight.current = false;
      onBusyChange?.(false);
      trackLoginEvent('login_code_failed');
      contactTracking.trackLogin('contact_login_failed', redirectAfterLogin, {
        contact_error_type: getContactErrorType(err),
      });
      if (isMounted.current) {
        notify.error(err instanceof Error ? err.message : undefined, 'login.tsx - verifyCode');
      }
      return;
    }

    // This outcome describes the verification API, before local session setup.
    trackLoginEvent('login_code_successful');
    contactTracking.trackLogin('contact_login_succeeded', redirectAfterLogin);
    if (!isMounted.current) {
      verifyInFlight.current = false;
      onBusyChange?.(false);
      return;
    }

    let clientStage = 'credentials';
    try {
      if (completeLogin) {
        await completeLogin(res);
        resetLoginState();
        return;
      }
      if (Platform.OS !== 'web') {
        await keychain.setAndWait('apiKey', res.token);
        if (!isMounted.current) return;

        clientStage = 'session';
        setSuccessful(true);

        if (res.was_created) {
          trackLoginEvent('create_user');
        }

        setApiKey(res.token);
        setUser(null);
        resetLoginState();
      }

      try {
        await monitoring.flushReplay();
      } catch (err) {
        contactTracking.trackLogin('contact_login_client_failed', redirectAfterLogin, {
          contact_login_stage: 'replay',
          contact_error_type: getContactErrorType(err),
        });
      }

      if (Platform.OS === 'web') {
        // Keep the current page's session stable until its final navigation.
        clientStage = 'credentials';
        keychain.set('apiKey', res.token);

        clientStage = 'session';
        notify.success(t('login.login_success'));
        setSuccessful(true);
        if (res.was_created) {
          trackLoginEvent('create_user');
        }
        resetLoginState();

        clientStage = 'redirect';
        if (redirectAfterLogin) {
          window.location.href = redirectAfterLogin;
          return;
        }

        window.location.reload();
        return;
      }

      clientStage = 'redirect';
      await router.replace(redirectAfterLogin ?? route('me.index'));
    } catch (err) {
      contactTracking.trackLogin('contact_login_client_failed', redirectAfterLogin, {
        contact_login_stage: clientStage,
        contact_error_type: getContactErrorType(err),
      });
      if (isMounted.current) {
        notify.error(err instanceof Error ? err.message : undefined, 'login.tsx - completeLogin');
      }
    } finally {
      verifyInFlight.current = false;
      onBusyChange?.(false);
    }
  };

  useImperativeHandle(submitRef, () => ({
    submit: () => verifyCode(codeInputRef.current?.getValue() ?? ''),
  }));

  const input = (
    <CodeInput
      testID="login-code-input"
      ref={codeInputRef}
      length={6}
      inputProps={{
        autoFocus: true,
        placeholder: t('login.enter_code'),
        accessibilityLabel: t('login.enter_code'),
      }}
      onEdit={() => api.clearErrors()}
      onComplete={(code) => verifyCode(code)}
    />
  );

  return (
    <>
      <LoginHeading
        title={isInWizard ? t('login.enter_code') : t('login.title')}
        subtitle={t('login.sms_code_sent', { number: phoneNumber })}
        isInModal={isInModal}
        isInWizard={isInWizard}
      />

      {isInModal && (
        <Text className={'text-brand-brown-dark mb-6'}>
          {t('login.sms_code_sent', { number: phoneNumber })}{' '}
          <Text
            accessibilityRole={'button'}
            className={'font-semibold underline'}
            onPress={successful ? undefined : onBack}
          >
            {t('login.change')}
          </Text>
        </Text>
      )}

      {isInWizard ? <View className={'mt-6'}>{input}</View> : input}

      <>
        <InputError errors={api.errors} />

        {!isInWizard && (
          <Button
            className={'mt-3'}
            text={t('login.verify_code')}
            icon={falAngleRight}
            onPress={() => verifyCode(codeInputRef.current!.getValue())}
            loading={api.loading}
            disabled={successful}
          />
        )}

        <ResendCode
          phoneNumber={phoneNumber}
          codeSentAt={codeSentAt}
          disabled={successful}
          onCodeSent={onCodeSent}
          redirectAfterLogin={redirectAfterLogin}
        />

        {!isInModal && (
          <Button
            className={'mt-3 self-center'}
            text={t('login.change_phone_number')}
            icon={falAngleLeft}
            iconPosition={'left'}
            color={'secondary'}
            dense={true}
            onPress={onBack}
            disabled={successful}
          />
        )}
      </>
    </>
  );
};

const ResendCode = ({
  phoneNumber,
  codeSentAt,
  disabled,
  onCodeSent,
  redirectAfterLogin,
}: {
  phoneNumber: string;
  codeSentAt: number | null;
  disabled: boolean;
  onCodeSent: (sentAt: number) => void;
  redirectAfterLogin: string | null;
}) => {
  const { t } = useTranslation('screens');
  const api = useApi({ notifyOnServerErrors: true, settleAfterUnmount: true });
  const sendInFlight = useRef(false);
  const isMounted = useRef(false);
  const { timeLeft, restart } = useCountdown(
    new Date((codeSentAt ?? Date.now()) + resendCodeCooldownMs),
  );

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const sendCode = async () => {
    if (sendInFlight.current || disabled || timeLeft.seconds > 0) return;
    sendInFlight.current = true;
    const sendParams = { contact_login_send_type: 'resend' };
    contactTracking.trackLogin('contact_login_code_started', redirectAfterLogin, sendParams);

    try {
      await api.request(apiRoute('authentication.send'), { phone_number: phoneNumber });
    } catch (err) {
      trackLoginEvent('login_resend_failed');
      contactTracking.trackLogin('contact_login_code_failed', redirectAfterLogin, {
        ...sendParams,
        contact_error_type: getContactErrorType(err),
      });
      if (isMounted.current) {
        notify.error(err instanceof Error ? err.message : undefined, 'login.tsx - resendCode');
      }
      return;
    } finally {
      sendInFlight.current = false;
    }

    trackLoginEvent('login_resend_sent');
    contactTracking.trackLogin('contact_login_code_sent', redirectAfterLogin, sendParams);
    if (!isMounted.current) return;
    const sentAt = Date.now();
    onCodeSent(sentAt);
    restart(new Date(sentAt + resendCodeCooldownMs));
  };

  return (
    <View className={'mt-3 flex-row items-center justify-center gap-3'}>
      <Text className={'text-brand-brown-dark/70 text-sm md:text-base'}>
        {t('login.resend_code_description')}
      </Text>

      <Button
        text={
          timeLeft.seconds > 0
            ? t('login.resend_code_countdown', { seconds: timeLeft.seconds })
            : t('login.resend_code')
        }
        icon={timeLeft.seconds > 0 ? undefined : falRotateLeft}
        iconPosition={'left'}
        color={'white'}
        dense={true}
        loading={api.loading}
        disabled={timeLeft.seconds > 0 || disabled}
        onPress={sendCode}
      />
    </View>
  );
};
